import moment from 'moment';

const CONF_CALL_APPOINTMENT_TYPE_ID = '9e5e4810-c5a8-428b-8f7d-71422b6b77e3';

const LATE_CHARGES_LINE =
  'Kindly confirm at your earliest convenience so we may finalize coverage.';

const LATE_CHARGES_STATEMENT =
  'Late charges of 0.56% per day are assessed on invoices over 30* days past due, (*60 days for work comp)';

const INTERPRETER_AVAILABILITY_LINE =
  'If interpreter availability or confirmed appointment duration requires an adjustment to the service scope, we will advise prior to finalizing coverage.';

const CANCELLATIONS_LINE =
  '*Cancellations less than 24 business hours notice incur full charge.';

const EMAIL_TRACKING_NOTICE = '[EMAIL_TRACKING_NOTICE]';

const ELA_SIGNATURE =
  'Executive Linguist Agency \n800-522-2320 | 310-376-1409 | 8am - 5pm Nationwide \nDiversified Language Services – Translation & Localization \nVideo Remote, Telephonic, and On - site Interpreting \nrequests: mail@executivelinguist.com';

// 2025-2026 fee schedule (matches the official rate sheet PDF)
const FEE_SCHEDULE = {
  'in-person': {
    spanish: {
      'non-certified':     { baseRate: 182.50, includedHours: 2.5, otRate: 55 },
      'medical certified': { baseRate: 300,    includedHours: 3,   otRate: 85 },
      'legal certified':   { halfDay: 390,  fullDay: 705,  halfDayHours: 3, fullDayHours: 6, otRate: 105 },
    },
    other: {
      'non-certified':     { baseRate: 360, includedHours: 3, otRate: 105 },
      'medical certified': { baseRate: 420, includedHours: 3, otRate: 125 },
      'legal certified':   { halfDay: 540,  fullDay: 1005, halfDayHours: 3, fullDayHours: 6, otRate: 155 },
    },
  },
  'remote': {
    spanish: {
      'non-certified':     { baseRate: 110, includedHours: 2, otRate: 55 },
      'medical certified': { baseRate: 170, includedHours: 2, otRate: 85 },
      'legal certified':   { halfDay: 315, fullDay: 630, halfDayHours: 3, fullDayHours: 6, otRate: 105 },
    },
    other: {
      'non-certified':     { baseRate: 210, includedHours: 2, otRate: 105 },
      'medical certified': { baseRate: 250, includedHours: 2, otRate: 125 },
      'legal certified':   { halfDay: 465, fullDay: 930, halfDayHours: 3, fullDayHours: 6, otRate: 155 },
    },
  },
  'conference call': {
    spanish: { baseRate: 15, includedMinutes: 10, otRate: 1.50 },
    other:   { baseRate: 25, includedMinutes: 10, otRate: 2.50 },
  },
};

// A "special" (blanket-auth) client has fees pre-authorized, so authorization
// emails omit the rate disclosure. Driven by the client's FeeAuth field.
export function isSpecialClient(feeAuth) {
  if (!feeAuth) return false;
  return feeAuth.toLowerCase().trim() === 'authorized';
}

function lookupJobType(appointmentTypeId) {
  if (!appointmentTypeId) return null;
  try {
    const allTypes = JSON.parse(localStorage.getItem('JobInvLineCodes')) || [];
    return (
      allTypes.find((jt) => {
        const id = jt.jobInvLineCodeId ?? jt.jobinvlinecodeid;
        return id != null && String(id).trim() === String(appointmentTypeId).trim();
      }) || null
    );
  } catch {
    return null;
  }
}

function getModality(jobType, appointmentTypeId) {
  if (String(appointmentTypeId).toLowerCase() === CONF_CALL_APPOINTMENT_TYPE_ID) return 'conference call';
  if (!jobType) return 'in-person';
  return jobType.appointmentTypeFlag === 2 ? 'remote' : 'in-person';
}

function getCertLevel(appointmentType) {
  const name = (appointmentType || '').toLowerCase();
  if (name.includes('legal') || name.includes('depo')) return 'legal certified';
  if (name.includes('medical')) return 'medical certified';
  return 'non-certified';
}

function getLanguageKey(language) {
  return (language || '').toUpperCase() === 'SPANISH' ? 'spanish' : 'other';
}

// Format money without locale commas; show decimals only when not a whole number.
// Matches real email style: $182.50, $300, $55, $105, $1005
function formatMoney(amount) {
  const num = Number(amount);
  return num % 1 === 0 ? String(num) : num.toFixed(2);
}

function lookupRates(modality, certLevel, langKey) {
  const modalityRates = FEE_SCHEDULE[modality];
  if (!modalityRates) return null;
  const langRates = modalityRates[langKey];
  if (!langRates) return null;
  if (modality === 'conference call') return langRates;
  return langRates[certLevel] || null;
}

// Label used inside the authorization question sentence
function certLabel(certLevel) {
  if (certLevel === 'non-certified') return 'non-certified/qualified';
  if (certLevel === 'medical certified') return 'medical certified';
  return 'Legal certified'; // legal certified — capital L matches real emails
}

function buildAuthorizationLines(modality, certLevel, langKey, language) {
  const rates = lookupRates(modality, certLevel, langKey);
  if (!rates) return [];

  if (modality === 'conference call') {
    return [
      `Our Standard fee for this service is $${formatMoney(rates.baseRate)} for up to ${rates.includedMinutes} minutes of ${language} conference call interpreting services, with additional time billed at $${formatMoney(rates.otRate)}/minute.*`,
    ];
  }

  if (certLevel === 'legal certified') {
    return [
      `Our Standard fee for this service is $${formatMoney(rates.halfDay)} for Half Day booking, up to ${rates.halfDayHours} hours, of Legal certified ${language} interpreting services.`,
      `Full Day booking is $${formatMoney(rates.fullDay)} for up to ${rates.fullDayHours} hours, with additional time billed at $${formatMoney(rates.otRate)}/hr.*`,
    ];
  }

  return [
    `Our Standard fee for this service is $${formatMoney(rates.baseRate)} for up to ${rates.includedHours} hours of ${modality} ${certLabel(certLevel)} ${language} interpreting services, with additional time billed at $${formatMoney(rates.otRate)}/hr.*`,
  ];
}
//test
function buildApptDetails(woData) {
  const safe = (v) => (v == null ? '' : String(v).trim());
  const date = moment(woData.jobDateTime).format('MM/DD/YYYY');
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const day = dayNames[moment(woData.jobDateTime).day()];
  const time = moment(woData.jobDateTime).format('hh:mm A');

  const lines = [
    `Job: ${woData.jobNum}`,
    `For: CLM # ${woData.clmClaimNumber}`,
    `On: ${date} (${day})  at ${time}`,
    `At: ${safe(woData.providerName)}`,
  ];

  const street = safe(woData.aptStreet);
  const city   = safe(woData.aptCity);
  const state  = safe(woData.aptState);
  const zip    = safe(woData.aptZip);

  if (street) {
    lines.push(`    ${street}`);
  }
  if (city || state || zip) {
    // Trailing space after zip matches the real email format
    lines.push(`    ${city}, ${state} ${zip} `);
  }

  return lines.join('\n');
}

export function buildAuthEmailSubject(woData) {
  const date = moment(woData.jobDateTime).format('MM/DD/YYYY');
  const time = moment(woData.jobDateTime).format('hh:mm A');
  return `${woData.clmFirstName} ${woData.clmLastName} CLM # ${woData.clmClaimNumber} - ${date} at ${time} - Please Reply All`;
}

// Subject for bulk (multi-job) authorization emails
export function buildAuthEmailSubjectMulti(jobs) {
  const seen = new Set();
  const parts = [];
  jobs.forEach((job) => {
    const key = job.clmClaimNumber;
    if (!seen.has(key)) {
      seen.add(key);
      parts.push(`${job.clmFirstName} ${job.clmLastName} CLM # ${job.clmClaimNumber}`);
    }
  });
  return `${parts.join(' | ')} - Please Reply All`;
}

// Body for bulk (multi-job) authorization emails.
// Jobs are grouped by rate (modality + cert level + language); each group shows
// its appointment details followed by the rate statement for that group.
export function buildAuthEmailBodyMulti(jobs, feeAuth, senderName) {
  if (!jobs || jobs.length === 0) return '';

  const first   = jobs[0];
  const special = isSpecialClient(feeAuth);
  const plural  = jobs.length > 1 ? 'appointments' : 'appointment';

  const lines = [`Good morning ${first.repFirstName},`, ''];

  if (special) {
    // Special clients: no rate disclosure — list all jobs together
    lines.push(`Do we have authorization to provide interpreting services for the ${plural} listed below? `);
    lines.push('');
    if (jobs.length > 1) {
      lines.push('Please click the green authorization button(s) to authorize a job(s).');
      lines.push('If you do not authorize one or more jobs please respond by email.');
      lines.push('If you wish to authorize all jobs at once, please click the Authorize all button at the bottom of the email.');
    } else {
      lines.push('Please click the green authorization button to authorize the job. If you do not authorize the job please respond by email.');
    }
    lines.push('');
    lines.push('[JOB_TABLE_START]');
    jobs.forEach((job, idx) => {
      lines.push(`[JOB_AUTH_START:${job.jobId}]`);
      lines.push(buildApptDetails(job));
      lines.push(`[JOB_AUTH_END:${job.jobId}]`);
      if (idx < jobs.length - 1) lines.push('');
    });
    lines.push('[JOB_TABLE_END]');
    lines.push('');
    lines.push(LATE_CHARGES_LINE);
  } else {
    const requester = first.callerNote;
    lines.push(
      requester
        ? `We received a request from ${requester} for interpreting services for the below ${plural}.`
        : `We received your request for interpreting services for the below ${plural}.`
    );
    lines.push('');
    lines.push(LATE_CHARGES_LINE);
    lines.push('');

    // Group jobs by their rate key: modality + certLevel + language
    const groups = new Map();
    jobs.forEach((job) => {
      const jobType  = lookupJobType(job.appointmentTypeId);
      const modality = getModality(jobType, job.appointmentTypeId);
      const certLvl  = getCertLevel(jobType?.appointmentType);
      const language = job.clmLanguage || '';
      const langKey  = getLanguageKey(language);
      const key      = `${modality}__${certLvl}__${langKey}`;

      if (!groups.has(key)) {
        groups.set(key, { modality, certLvl, language, langKey, jobs: [] });
      }
      groups.get(key).jobs.push(job);
    });

    const groupList = Array.from(groups.values());
    groupList.forEach((group, groupIdx) => {
      lines.push('[JOB_TABLE_START]');
      group.jobs.forEach((job, jobIdx) => {
        lines.push(`[JOB_AUTH_START:${job.jobId}]`);
        lines.push(buildApptDetails(job));
        lines.push(`[JOB_AUTH_END:${job.jobId}]`);
        if (jobIdx < group.jobs.length - 1) lines.push('');
      });
      lines.push('[JOB_TABLE_END]');

      const authLines = buildAuthorizationLines(group.modality, group.certLvl, group.langKey, group.language);
      if (authLines.length) {
        lines.push('');
        lines.push(...authLines);
      }

      // Blank line between groups
      if (groupIdx < groupList.length - 1) lines.push('');
    });

    lines.push('');
    lines.push(INTERPRETER_AVAILABILITY_LINE);
    lines.push('');
    lines.push(CANCELLATIONS_LINE);
  }

  lines.push('');
  lines.push('[AUTHORIZE_ALL]');
  lines.push('[LINK_EXPIRY_NOTE]');
  lines.push(EMAIL_TRACKING_NOTICE);
  lines.push('');
  lines.push(' Thank you,');
  lines.push('');
  lines.push(senderName || '');
  lines.push('');
  lines.push(ELA_SIGNATURE);

  return lines.join('\n');
}

export function buildRepIconEmailBody(woData, senderName) {
  const lines = [
    `Dear ${woData.repFirstName},`,
    '',
    buildApptDetails(woData),
    '',
    LATE_CHARGES_STATEMENT,
    '',
    '<em style="font-size:12px;color:#666;">*Authorization links expire after 7 days.</em>',
    '<em style="font-size:12px;color:#666;">*This email uses open tracking, which records your IP address, email client, and the exact time this message is opened.</em>',
    '',
    ' Thank you,',
    '',
    senderName || '',
    '',
    ELA_SIGNATURE,
  ];
  return lines.join('\n');
}

export function buildAuthEmailBody(woData, feeAuth, senderName) {
  const jobType  = lookupJobType(woData.appointmentTypeId);
  const modality = getModality(jobType, woData.appointmentTypeId);
  const certLvl  = getCertLevel(jobType?.appointmentType);
  const language = woData.clmLanguage || '';
  const langKey  = getLanguageKey(language);
  const special   = isSpecialClient(feeAuth);

  const lines = [`Good morning ${woData.repFirstName},`, ''];

  if (special) {
    // Blanket-auth clients (FeeAuth = Authorized) — no rate disclosure needed
    lines.push('Do we have authorization to provide interpreting services for the appointment listed below? ');
    lines.push('');
    lines.push('Please click the green authorization button to authorize the job. If you do not authorize the job please respond by email.');
    lines.push('');
    lines.push('[JOB_TABLE_START]');
    lines.push(`[JOB_AUTH_START:${woData.jobId}]`);
    lines.push(buildApptDetails(woData));
    lines.push(`[JOB_AUTH_END:${woData.jobId}]`);
    lines.push('[JOB_TABLE_END]');
    lines.push('');
    lines.push(LATE_CHARGES_LINE);
  } else {
    const requester = woData.callerNote;
    lines.push(
      requester
        ? `We received a request from ${requester} for interpreting services for the below appointment.`
        : 'We received your request for interpreting services for the below appointment.'
    );
    lines.push('');
    lines.push(LATE_CHARGES_LINE);
    lines.push('');
    lines.push('[JOB_TABLE_START]');
    lines.push(`[JOB_AUTH_START:${woData.jobId}]`);
    lines.push(buildApptDetails(woData));
    lines.push(`[JOB_AUTH_END:${woData.jobId}]`);
    lines.push('[JOB_TABLE_END]');
    lines.push('');

    const authLines = buildAuthorizationLines(modality, certLvl, langKey, language);
    if (authLines.length) {
      lines.push(...authLines);
      lines.push('');
    }

    lines.push(INTERPRETER_AVAILABILITY_LINE);
    lines.push('');
    lines.push(CANCELLATIONS_LINE);
  }

  lines.push('');
  lines.push('[LINK_EXPIRY_NOTE]');
  lines.push(EMAIL_TRACKING_NOTICE);
  lines.push('');
  lines.push(' Thank you,');
  lines.push('');
  lines.push(senderName || '');
  lines.push('');
  lines.push(ELA_SIGNATURE);

  return lines.join('\n');
}
